import React, { useContext } from 'react'
import styled, { css } from 'styled-components'
import { useSpring, animated } from 'react-spring'
import { StateContext } from 'store/contexts'
import {
  theme,
  TOPBAR_HEIGHT,
  MOBILE_TOPBAR_HEIGHT,
  SCREEN_DEFAULT_COLOR,
  useWindowSize,
  getResponsiveStyles,
} from 'global'
import { ReactComponent as Logo } from 'svgs/logo.svg'
import { Flex, Box, Filigree, QuestionsProgressBar } from 'components'
import { getFiligreeStyles } from './filigreeStyles'
import { currentQuestionSelector } from 'store/selectors'

const DEV_ENV = process.env.NODE_ENV === 'development'

// Emulating Webflow Header
const DevNav = styled.header`
  position: fixed;
  width: 100%;
  background: white;
  z-index: 10000;
  height: 90px;
  /* mobile */
  @media screen and (max-width: 991px) {
    height: 60px;
  }
`

const LayoutMainContainer = styled(Flex)`
  /* I don't think this is being respected on all devices / browser version, but I'll put it here
  just incase */
  touch-action: manipulation;
  position: absolute;
  min-height: ${`calc(100vh - ${TOPBAR_HEIGHT}px);`};
  ${DEV_ENV &&
  css`
    margin-top: ${TOPBAR_HEIGHT}px;
  `}

  /* mobile */
  @media screen and (max-width: 991px) {
    min-height: ${`calc(100% - ${MOBILE_TOPBAR_HEIGHT}px)`};
    ${DEV_ENV &&
    css`
      margin-top: ${MOBILE_TOPBAR_HEIGHT}px;
    `}
  }
`

const LayoutBackground = styled(Box)`
  position: absolute;
  width: 100%;
  height: 100%;
`

const WithNav = ({ children }: { children: React.ReactNode }) => {
  return (
    <React.Fragment>
      <DevNav>
        <Logo /> nav
      </DevNav>
      {children}
    </React.Fragment>
  )
}

interface ILayout {
  children: React.ReactNode
}

export const Layout = ({ children }: ILayout) => {
  const state = useContext(StateContext)
  const { filteredQuestions, currentQuestionIndex } = state.questions
  const { width } = useWindowSize()

  const currentQuestion =
    state.screen === 'QuestionsScreen'
      ? currentQuestionSelector(state)
      : undefined

  // Keep relevant filigree styles during question submit (see comments on QuestionsScreen.tsx re:
  // currentQuestion).
  const uiCurrentQuestionIndex =
    currentQuestionIndex === filteredQuestions.length
      ? currentQuestionIndex - 1
      : currentQuestionIndex

  const filigreeStyles = getFiligreeStyles(state.screen)(uiCurrentQuestionIndex)

  const { transform, ...restFiligreeStyles } = filigreeStyles

  // Transform is not supported by styled-system and for the useSpring animation we need to select
  // the transform for the current breakpoint. Therefore custom methods have been created to
  // mimmic the styled system breakpoints logic with JS.
  const responsiveBaseTransform = getResponsiveStyles({ transform }, width)
  const baseStyles = { ...responsiveBaseTransform, ...restFiligreeStyles }

  const filigreeAnimProps = useSpring({
    from: {},
    to: {
      ...responsiveBaseTransform,
      ...state.ui.filigree,
    },
    immediate: state.screen !== 'HandScreen',
  })

  const backgroundAnimProps = useSpring({
    from: {},
    to: {
      opacity: 1,
      ...state.ui.digestBg,
    },
  })

  const baseLayout = (
    <LayoutMainContainer
      width={1}
      alignContent='stretch'
      justifyContent='center'
    >
      {/* Background color panel rendered on all pages */}
      <animated.div style={backgroundAnimProps}>
        <LayoutBackground
          zIndex={theme.zIndices.background}
          backgroundColor={
            currentQuestion?.theming
              ? currentQuestion.theming.color
              : SCREEN_DEFAULT_COLOR[state.screen]
          }
        />
      </animated.div>

      <Flex width={1}>
        {state.screen === 'QuestionsScreen' && <QuestionsProgressBar />}
        <Flex
          width={1}
          zIndex={theme.zIndices.foreground}
          overflow='hidden'
          alignItems='center'
          justifyContent='center'
        >
          <>
            <Filigree style={filigreeAnimProps} {...baseStyles} />
            {children}
          </>
        </Flex>
      </Flex>
    </LayoutMainContainer>
  )

  if (DEV_ENV) {
    return <WithNav>{baseLayout}</WithNav>
  } else {
    return baseLayout
  }
}
